import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { PlaceTypesService } from '../services/place-types.service';
import * as PlaceTypeActions from './place-types.actions';

@Injectable()
export class PlaceTypesEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private _logger = inject(LoggerService);
    private _placeTypeService = inject(PlaceTypesService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all place types',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // ------------------------------------------------------------------------------------------
    // @ Effects
    // ------------------------------------------------------------------------------------------

    // Get All PlaceTypes
    getAllPlaceTypes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaceTypeActions.loadPlaceTypes),
            switchMap(() => {
                return this._placeTypeService.getAll().pipe(
                    map((res) => {
                        return PlaceTypeActions.loadPlaceTypesSuccess({
                            placetypes: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaceTypeActions.loadPlaceTypesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One PlaceType
    getPlaceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaceTypeActions.loadPlaceType),
            switchMap((action) => {
                return this._placeTypeService.getOne(action.id).pipe(
                    map((res) => {
                        return PlaceTypeActions.loadPlaceTypeSuccess({
                            placetype: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaceTypeActions.loadPlaceTypeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create PlaceType
    createPlaceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaceTypeActions.createPlaceType),
            switchMap((action) => {
                return this._placeTypeService.create(action.data).pipe(
                    map((res) => {
                        return PlaceTypeActions.createPlaceTypeSuccess({
                            placetype: res.data,
                            success: 'Place Type created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaceTypeActions.createPlaceTypeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update PlaceType
    updatePlaceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaceTypeActions.updatePlaceType),
            switchMap((action) => {
                return this._placeTypeService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return PlaceTypeActions.updatePlaceTypeSuccess({
                                placetype: res.data,
                                success: 'Place Type updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                PlaceTypeActions.updatePlaceTypeFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete PlaceType
    deletePlaceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaceTypeActions.deletePlaceType),
            switchMap((action) => {
                return this._placeTypeService.delete(action.id).pipe(
                    map(() => {
                        return PlaceTypeActions.deletePlaceTypeSuccess({
                            id: action.id,
                            success: 'Place Type deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaceTypeActions.deletePlaceTypeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // ------------------------------------------------------------------------------------------
    // @ Alerts
    // ------------------------------------------------------------------------------------------

    onPlaceTypeActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    PlaceTypeActions.createPlaceTypeSuccess,
                    PlaceTypeActions.updatePlaceTypeSuccess,
                    PlaceTypeActions.deletePlaceTypeSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onPlaceTypeActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    PlaceTypeActions.loadPlaceTypesFailure,
                    PlaceTypeActions.loadPlaceTypeFailure,
                    PlaceTypeActions.createPlaceTypeFailure,
                    PlaceTypeActions.updatePlaceTypeFailure,
                    PlaceTypeActions.deletePlaceTypeFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
